import moment from 'moment';
import { useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { Spinner } from '../../../../../components/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { getDaysInMonth, nextYears, previousYears } from '../../../../../utils/dates';
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { toast } from 'react-toastify';
import { makeRequest } from '../../../../../utils/makeRequest';

function NewFundingNextRepaymentDateForm({
  fundingId,
  fundingNextRepaymentDate,
  fundingRepaymentMode,
  refreshFundingData,
}) {
  const { apiOriginFunding } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [spinner, setSpinner] = useState(false);

  const [nextRepaymentDate, setNextRepaymentDate] = useState({
    year: String(moment(fundingNextRepaymentDate).year()),
    month: String(moment(fundingNextRepaymentDate).month()),
    date: String(moment(fundingNextRepaymentDate).date()),
  });

  const handleToggle = () => {
    setIsOpen(prev => !prev);
    setNextRepaymentDate({
      year: String(moment(fundingNextRepaymentDate).year()),
      month: String(moment(fundingNextRepaymentDate).month()),
      date: String(moment(fundingNextRepaymentDate).date()),
    });
  };

  const setRapaymentDate = async reset => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/set_repayment_date`,
      method: 'POST',
      data: JSON.stringify({
        fundingId,
        repaymentDate: reset
          ? null
          : moment({
              year: String(moment(nextRepaymentDate).year()),
              month: String(moment(nextRepaymentDate).month()),
              date: String(moment(nextRepaymentDate).date()),
            }).format('YYYY-MM-DD'),
      }),
    };

    try {
      await makeRequest(config);
      await refreshFundingData();

      handleToggle();

      toast.success('Next repayment date updated successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  return (
    <div className="d-flex align-items-center gap-2">
      <p className="bo-common-text">
        {fundingNextRepaymentDate
          ? moment.utc(fundingNextRepaymentDate).local().format('DD MMM, YYYY')
          : '---'}
      </p>
      <OverlayTrigger
        trigger="click"
        placement="bottom-end"
        show={isOpen}
        popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 0] } }] }}
        overlay={
          <Popover className="custom-popover">
            <Popover.Body className="bo-common-popover-form-menu">
              <Spinner visible={spinner} size={36}>
                <Form.Label className="bo-common-popover-form-menu-label">Year</Form.Label>
                <Form.Select
                  className="bo-common-popover-form-menu-input bo-w-180"
                  value={nextRepaymentDate.year}
                  onChange={e => {
                    if (
                      !moment({
                        year: e.target.value,
                        month: nextRepaymentDate.month,
                        date: nextRepaymentDate.date,
                      }).isValid()
                    ) {
                      setNextRepaymentDate({
                        ...nextRepaymentDate,
                        year: e.target.value,
                        date: moment({ year: e.target.value, month: nextRepaymentDate.month })
                          .endOf('month')
                          .date(),
                      });
                    } else {
                      setNextRepaymentDate({ ...nextRepaymentDate, year: e.target.value });
                    }
                  }}
                >
                  {[...previousYears(10), ...nextYears(20, moment().add(1, 'year').year())].map(
                    year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ),
                  )}
                </Form.Select>
                <Form.Label className="bo-common-popover-form-menu-label mt-2">Month</Form.Label>
                <Form.Select
                  className="bo-common-popover-form-menu-input bo-w-180"
                  value={nextRepaymentDate.month}
                  onChange={e => {
                    if (
                      !moment({
                        year: nextRepaymentDate.year,
                        month: e.target.value,
                        date: nextRepaymentDate.date,
                      }).isValid()
                    ) {
                      setNextRepaymentDate({
                        ...nextRepaymentDate,
                        month: e.target.value,
                        date: moment({ year: nextRepaymentDate.year, month: e.target.value })
                          .endOf('month')
                          .date(),
                      });
                    } else {
                      setNextRepaymentDate({ ...nextRepaymentDate, month: e.target.value });
                    }
                  }}
                >
                  {[...Array(12)].map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <option value={i} key={i}>
                      {moment().month(i).format('MMMM')}
                    </option>
                  ))}
                </Form.Select>
                <Form.Label className="bo-common-popover-form-menu-label mt-2">Day</Form.Label>
                <Form.Select
                  className="bo-common-popover-form-menu-input bo-w-180"
                  value={nextRepaymentDate.date}
                  onChange={e =>
                    setNextRepaymentDate({ ...nextRepaymentDate, date: e.target.value })
                  }
                >
                  {getDaysInMonth(nextRepaymentDate.month, nextRepaymentDate.year).map(x => (
                    <option value={x} key={x}>
                      {x}
                    </option>
                  ))}
                </Form.Select>
                <div className="mt-2 d-flex justify-content-between">
                  <Button className="bo-common-button px-2" onClick={() => setRapaymentDate()}>
                    Set
                  </Button>
                  {fundingRepaymentMode === 'MANUAL' && (
                    <Button
                      className="bo-common-button px-2"
                      onClick={() => setRapaymentDate(true)}
                    >
                      Reset
                    </Button>
                  )}
                  <Button className="bo-common-button px-2" onClick={handleToggle}>
                    Cancel
                  </Button>
                </div>
              </Spinner>
            </Popover.Body>
          </Popover>
        }
      >
        <Button
          className="bo-common-base-button"
          onClick={handleToggle}
          disabled={fundingRepaymentMode === 'CUSTOM' || fundingRepaymentMode === 'FORCE'}
        >
          <FontAwesomeIcon icon={faPencil} color="#5946A4" size="sm" />
        </Button>
      </OverlayTrigger>
    </div>
  );
}

export default NewFundingNextRepaymentDateForm;
